import React from "react";
import Figure from "./Figure";

// The twelve moves, narrated. All twelve are rendered and eleven are `hidden`,
// exactly as the server renders them — so the markup a reader gets with this
// file and without it is the same shape, and the browser tests do not have to
// know which one they are looking at.
//
// The controls go DISABLED at the ends rather than disappearing. A control
// that vanishes when you reach the end of a sequence drops focus onto <body>,
// and the reader who pressed Next eleven times is exactly the person standing
// on it.
export default function Walk({
  steps,
  figures,
  step,
  playing,
  stage,
  assembly,
  onStep,
  onPlay,
  onFinish
}) {
  const last = steps.length - 1;
  const current = steps[step];

  return (
    <section className="geo-walk" aria-labelledby="geo-walk-heading" data-testid="geomancy-walk">
      <div className="geo-walk__head">
        <h2 className="section-index__heading" id="geo-walk-heading">
          How this shield was made
        </h2>
        <p className="geo-walk__count" data-testid="geo-walk-count">
          Step <span data-walk-number>{step + 1}</span> of {steps.length}
        </p>
        {/* The announcement is its own small line: with the live region on the
            panel, arriving at a step read out the move, two figures, a
            five-row table and a paragraph of the 1655 English. */}
        <p className="visually-hidden" role="status">
          {current ? `Step ${step + 1} of ${steps.length}: ${current.heading}` : ""}
        </p>
      </div>

      <div className="geo-walk__nav">
        <button
          type="button"
          className="geo-walk__button"
          data-walk-previous
          disabled={step === 0}
          onClick={() => onStep(step - 1)}
        >
          <Chevron direction="left" /> Previous
        </button>
        <button
          type="button"
          className="geo-walk__button geo-walk__button--play"
          data-walk-play
          data-testid="geo-walk-play"
          aria-pressed={playing}
          onClick={onPlay}
        >
          {playing ? <Pause /> : <Play />}{" "}
          <span data-walk-play-label>{playing ? "Stop" : "Watch it build"}</span>
        </button>
        <button
          type="button"
          className="geo-walk__button"
          data-walk-next
          disabled={step >= last}
          onClick={() => onStep(step + 1)}
        >
          Next <Chevron direction="right" />
        </button>
        <button type="button" className="geo-walk__whole" onClick={onFinish}>
          Show the finished shield
        </button>
      </div>

      <div className="geo-walk__steps" data-testid="geo-walk-step">
        {steps.map((one) => (
          <article
            key={one.index}
            className="geo-walk__step"
            data-walk-step={one.index}
            data-testid={`geo-walk-step-${one.index}`}
            hidden={one.index !== step}
          >
            <h3 className="geo-walk__step-heading">{one.heading}</h3>
            {one.index === step ? stage : null}
            <p className="geo-walk__summary">{one.summary}</p>

            <div className="geo-walk__working">
              {one.sources.length > 0 ? (
                <ol className="geo-walk__sources">
                  {one.sources.map((source) => (
                    <li key={source.note}>
                      <Figure figure={figures[source.figure]} size={34} />
                      <span className="geo-walk__source-note">{source.note}</span>
                      <span className="geo-walk__source-name">
                        {figures[source.figure].printedName}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : null}

              <div className="geo-walk__result">
                {one.made.map((slug) => (
                  <div className="geo-walk__made" key={slug}>
                    <Figure figure={figures[slug]} size={46} />
                    <span className="geo-walk__made-name">{figures[slug].printedName}</span>
                  </div>
                ))}
              </div>
            </div>

            {one.index === step ? assembly : null}

            {one.addition ? (
              <table className="geo-add" data-testid={`geo-add-step-${one.index}`}>
                <caption className="visually-hidden">
                  {one.sources[0].note} added to {one.sources[1].note}, line by line
                </caption>
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="visually-hidden">Line</span>
                    </th>
                    <th scope="col">{one.sources[0].note}</th>
                    <th scope="col">{one.sources[1].note}</th>
                    <th scope="col">Points</th>
                    <th scope="col">Leaves</th>
                  </tr>
                </thead>
                <tbody>
                  {one.addition.map((line, index) => (
                    <tr key={index}>
                      <th scope="row" className="geo-add__line">
                        {index + 1}
                      </th>
                      <td>
                        <span className="geo-add__dots">{"·".repeat(line.first)}</span>
                      </td>
                      <td>
                        <span className="geo-add__dots">{"·".repeat(line.second)}</span>
                      </td>
                      <td>
                        {line.total} —{" "}
                        <span className="geo-add__parity">
                          {line.total % 2 === 0 ? "even" : "odd"}
                        </span>
                      </td>
                      <td>
                        <span className="geo-add__dots geo-add__dots--result">
                          {"·".repeat(line.dots)}
                        </span>
                        <span className="geo-add__result-word">
                          {line.dots === 2 ? "two" : "one"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {one.passage ? (
              <figure className="passage" data-testid={`passage-${one.passage.key}`}>
                <blockquote className="passage__quote">{one.passage.quote}</blockquote>
                <figcaption className="passage__source">
                  {one.passage.locator} — in the{" "}
                  <cite>Fourth Book of Occult Philosophy</cite>, Robert Turner&rsquo;s
                  English, London 1655
                </figcaption>
                <p className="passage__gloss">
                  <span className="passage__gloss-label">In plain English</span>
                  {one.passage.paraphrase}
                </p>
              </figure>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

// The same geometric vocabulary as IconsHelper, drawn here because this file
// cannot call a Ruby helper. Decoration beside a word, so aria-hidden.
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};

function Chevron({ direction }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
      <path d={direction === "left" ? "M14.5 5.5 8 12l6.5 6.5" : "M9.5 5.5 16 12l-6.5 6.5"} />
    </svg>
  );
}

function Play() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
      <path d="M8.5 5.5 18 12l-9.5 6.5z" />
    </svg>
  );
}

function Pause() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" {...stroke}>
      <path d="M9.5 5.5v13M14.5 5.5v13" />
    </svg>
  );
}
